import { ArrowRight, Globe, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { RelativeTime } from '@/components/custom-ui/RelativeTime'
import { PageShell } from '@/components/layout/PageShell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/sonner'
import { Textarea } from '@/components/ui/textarea'
import { ExtensionGate } from '@/features/extensions/ExtensionGate'
import {
  useCreateOsintInvestigation,
  useOsintInvestigations,
} from '@/features/osint/hooks/use-osint'
import {
  OsintEgressCard,
  OsintEgressDialog,
  useOsintEgressGate,
} from '@/features/osint/OsintEgressNotice'
import { OSINT_EXTENSION_ID } from '@/hooks/use-extensions'
import { useCaseContext } from '@/pages/cases/CaseContext'
import type { OsintInvestigation } from '@/services/api/osint'

export default function CaseOsintPage() {
  const { t } = useTranslation()
  const { caseId } = useCaseContext()
  const navigate = useNavigate()
  const [brief, setBrief] = useState('')

  const { data: investigations = [], isLoading } = useOsintInvestigations(caseId)
  const create = useCreateOsintInvestigation(caseId)
  const egress = useOsintEgressGate(caseId)

  const runInvestigation = async () => {
    const text = brief.trim()
    if (!text) return
    try {
      const inv = await create.mutateAsync({ title: text.slice(0, 120), brief: text })
      setBrief('')
      navigate(`/cases/${caseId}/osint/${inv.id}`)
    } catch (_err) {
      toast.error(t('osint.startError'))
    }
  }

  // First run in a case goes through the egress acknowledgement; afterwards the
  // ambient card above the composer carries the reminder.
  const start = () => egress.guard(runInvestigation)
  const canStart = brief.trim().length > 0 && !create.isPending

  // The nav hides this workspace while the integration is off — the disabled
  // card covers direct URLs and bookmarks (and the composer stays back until
  // the extension list has answered).
  return (
    <PageShell
      layout="full"
      icon={Globe}
      title={t('nav.subviews.osint')}
      summary={t('osint.summary')}
    >
      <div className="flex flex-1 flex-col overflow-y-auto px-4 pb-8 md:px-6">
        <ExtensionGate
          extensionId={OSINT_EXTENSION_ID}
          titleKey="osint.integrationDisabled.title"
          bodyKey="osint.integrationDisabled.description"
        >
          <div className="w-full space-y-6">
            <OsintEgressCard />

            {/* Textarea and action on one row. The button carries no label — it
              stretches to the field's height (which grows with the draft), so a
              caption would only stretch with it. */}
            <div className="flex gap-3">
              <Textarea
                value={brief}
                onChange={e => setBrief(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canStart) {
                    e.preventDefault()
                    start()
                  }
                }}
                placeholder={t('osint.composerPlaceholder')}
                rows={3}
                className="flex-1 resize-none"
              />
              <Button
                onClick={start}
                disabled={!canStart}
                aria-label={t('osint.start')}
                title={t('osint.start')}
                className="h-auto w-14 shrink-0 rounded-lg px-0"
              >
                {create.isPending ? (
                  <Loader2 className="size-5 animate-spin" />
                ) : (
                  <ArrowRight className="size-5" />
                )}
              </Button>
            </div>

            <div className="space-y-2">
              <h2 className="text-sm font-medium text-muted-foreground">{t('osint.recent')}</h2>
              {isLoading ? (
                <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
              ) : investigations.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('osint.empty')}</p>
              ) : (
                <div className="space-y-2">
                  {investigations.map(inv => (
                    <OsintRow
                      key={inv.id}
                      investigation={inv}
                      onOpen={() => navigate(`/cases/${caseId}/osint/${inv.id}`)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </ExtensionGate>
      </div>

      <OsintEgressDialog open={egress.open} onConfirm={egress.confirm} onCancel={egress.cancel} />
    </PageShell>
  )
}

function OsintRow({
  investigation,
  onOpen,
}: {
  investigation: OsintInvestigation
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const running =
    investigation.latestRun?.status === 'running' || investigation.latestRun?.status === 'queued'
  const failed = investigation.latestRun?.status === 'finished_error'
  const needsInput = investigation.latestRun?.status === 'awaiting_input'

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-3 rounded-lg border border-border bg-card px-3 py-3 text-left transition-colors hover:border-primary/40 hover:bg-muted/40 max-md:flex-wrap max-md:gap-y-2 md:px-4"
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Globe className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground max-md:line-clamp-2 md:truncate">
          {investigation.title}
        </p>
        <p className="truncate text-xs text-muted-foreground">{investigation.brief}</p>
      </div>
      {/* Phone: status and age sit on their own row so the title keeps the width. */}
      <div className="flex shrink-0 items-center gap-3 max-md:w-full max-md:justify-between max-md:pl-11">
        {needsInput ? (
          <Badge variant="warning">{t('osint.status.awaitingInput')}</Badge>
        ) : running ? (
          <Badge variant="info">
            <span className="size-1.5 animate-pulse rounded-full bg-current" aria-hidden />
            {t('osint.status.running')}
          </Badge>
        ) : failed ? (
          <Badge variant="destructive">{t('osint.status.failed')}</Badge>
        ) : (
          <Badge variant="success">{t('osint.status.completed')}</Badge>
        )}
        <span className="text-2xs text-muted-foreground">
          <RelativeTime at={investigation.updatedAt} />
        </span>
      </div>
    </button>
  )
}
